/**
 * The paper that text sits on, over the generated field.
 *
 * ── This is the legibility mechanism ─────────────────────────────────────────
 * Not the shader's band fade, not the still's baked-in lightness — this. It is
 * deliberately dull: a plain opaque region in the tree that every background
 * implementation renders identically, because the alternative has already
 * shipped a bug.
 *
 * What happened on mobile (gotchas.md #8): there were two backgrounds, a live
 * SkSL shader that thinned its own lines under the text and a still image that
 * could not. The legibility suite modelled the shader's fade, so it gave the
 * still credit for a mechanism it did not have, and a headline shipped sitting
 * on full-strength line art. The model was internally consistent, fully tested,
 * and wrong.
 *
 * The web now has THREE background states — live WebGL2, the baked still, and
 * not-yet-drawn — so the same rule binds harder here, not less.
 *
 * The plate is fully opaque before the first text block. That makes contrast
 * ink-on-page (~13.7:1) regardless of what the field is doing behind it. The
 * long feather above is what stops it reading as a panel bolted over the
 * artwork: the lines are already thinning as the paper comes up to meet them,
 * so the art dissolves into the page rather than being covered by it.
 */

export type TextPlate = {
  /** Fraction of height where the paper begins to come up. */
  featherStart: number;
  /** Fraction by which the paper is fully opaque. Always above the topmost text,
   *  so every text block sits on solid page colour. */
  opaqueFrom: number;
};

/** Opacity of the paper at a given fraction of height. Smoothstep, so the ramp
 *  has no visible edge at either end. */
export function plateAlphaAt(heightFraction: number, plate: TextPlate): number {
  const f = Math.min(Math.max(heightFraction, 0), 1);
  if (f <= plate.featherStart) return 0;
  if (f >= plate.opaqueFrom) return 1;
  const t = (f - plate.featherStart) / (plate.opaqueFrom - plate.featherStart);
  return t * t * (3 - 2 * t);
}

/**
 * Gradient stops for the plate, in its own 0..1 space, as [location, alpha].
 *
 * Sampled rather than handed to the gradient as two stops because a linear
 * interpolation between 0 and 1 is not a smoothstep, and the straight ramp
 * shows a faint band where it meets the solid region.
 */
export function plateGradientStops(plate: TextPlate, steps = 12): [number, number][] {
  const span = 1 - plate.featherStart;
  const solidAt = (plate.opaqueFrom - plate.featherStart) / span;
  const stops: [number, number][] = [];
  for (let i = 0; i <= steps; i += 1) {
    const location = (i / steps) * solidAt;
    const t = i / steps;
    stops.push([location, t * t * (3 - 2 * t)]);
  }
  stops.push([1, 1]);
  return stops;
}

/**
 * What the paper is painted with: the page colour as `r, g, b` (for contexts
 * with no stylesheet — mobile, a baked still), or a function from alpha to any
 * CSS colour, which is how the web hands it `var(--rt-page)` so the paper
 * follows the theme instead of being frozen at the light page.
 */
export type PlatePaint = string | ((alpha: number) => string);

function paintAt(paint: PlatePaint): (alpha: number) => string {
  return typeof paint === 'string' ? (a) => `rgba(${paint}, ${a.toFixed(4)})` : paint;
}

/**
 * The plate as a CSS `linear-gradient(...)`. Web-side convenience over the
 * same stops the app feeds to expo-linear-gradient.
 */
export function plateGradientCss(plate: TextPlate, paint: PlatePaint, steps = 12): string {
  const colourAt = paintAt(paint);
  const stops = plateGradientStops(plate, steps)
    .map(([location, alpha]) => `${colourAt(alpha)} ${(location * 100).toFixed(2)}%`)
    .join(', ');
  return `linear-gradient(to bottom, ${stops})`;
}

/**
 * The same paper, shaped as a pool around a block of copy instead of a band
 * across the bottom of it.
 *
 * The band plate above assumes the art lives above the text and the page takes
 * over below it. The landing hero no longer works that way: the field runs the
 * full height of the section and the copy sits inside it, so the paper has to
 * open up around the text and let the lines close back in on every side.
 *
 * Same rule as the band, for the same reason: this is a plain element in the
 * tree that every background state renders identically. Nothing about
 * legibility may depend on what the shader is doing.
 *
 * ── Why the pool is a background-size and not a gradient radius ──────────────
 * Chrome rejects `clamp()` — or `min()`, `max()`, `calc()` — as a
 * radial-gradient's size. Not "renders oddly": the whole declaration is invalid,
 * so assigning it drops `background-image` to `none` and the paper vanishes
 * while the element stays right there in the tree, correct-looking to any test
 * that asks whether the plate exists. It shipped exactly that way for one run
 * of this suite, and only the pixel-level assertion in
 * `web/tests/e2e/rendered-legibility.spec.ts` noticed.
 *
 * The pool needs to clamp, because the copy column is a fixed pixel width until
 * the viewport is narrower than it: a pure percentage tracks neither, and slides
 * the paper out from under the subhead somewhere in between. So the gradient is
 * given a plain `50% 50%` — the pool always fills its own box — and the clamp
 * moves out to `background-size`, which takes math functions perfectly happily.
 */
export type TextHalo = {
  /** Centre of the pool within its box, in fractions of the box. */
  cx: number;
  cy: number;
  /** Width of the pool — a CSS `<length-percentage>` for `background-size`, so
   *  `clamp()` and friends are allowed here and nowhere else in this type. */
  width: string;
  /** Height of the pool, same units. */
  height: string;
  /** Fraction of the radius that is solid paper, before the feather starts. */
  opaqueTo: number;
};

/**
 * Normalised elliptical distance of a point from the pool's centre: 0 at the
 * centre, 1 on the rim. `dx`/`dy` and `rx`/`ry` must be in the same units —
 * resolve the CSS width and height and halve them before calling.
 */
export function haloDistance(dx: number, dy: number, rx: number, ry: number): number {
  const u = dx / rx;
  const v = dy / ry;
  return Math.sqrt(u * u + v * v);
}

/** Opacity of the paper at a normalised elliptical distance. Smoothstep, so the
 *  pool has no visible rim where it meets the field. */
export function haloAlphaAt(distance: number, halo: Pick<TextHalo, 'opaqueTo'>): number {
  const d = Math.max(distance, 0);
  if (d <= halo.opaqueTo) return 1;
  if (d >= 1) return 0;
  const t = (d - halo.opaqueTo) / (1 - halo.opaqueTo);
  return 1 - t * t * (3 - 2 * t);
}

/** Gradient stops for the pool, as [location along the radius, alpha]. Sampled
 *  for the same reason `plateGradientStops` is — two stops interpolate linearly
 *  and the straight ramp shows a rim. */
export function haloGradientStops(halo: Pick<TextHalo, 'opaqueTo'>, steps = 14): [number, number][] {
  const stops: [number, number][] = [];
  for (let i = 0; i <= steps; i += 1) {
    const location = halo.opaqueTo + (i / steps) * (1 - halo.opaqueTo);
    stops.push([location, haloAlphaAt(location, halo)]);
  }
  return [[0, 1], ...stops];
}

/**
 * The pool as background properties (`paint`: see {@link PlatePaint}). Spread
 * onto the element rather than assembled at the call site: the split between the
 * gradient and `background-size` is a browser constraint, not a styling choice,
 * and it only holds if all four land together.
 */
export function haloBackground(halo: TextHalo, paint: PlatePaint, steps = 14) {
  const colourAt = paintAt(paint);
  const stops = haloGradientStops(halo, steps)
    .map(([location, alpha]) => `${colourAt(alpha)} ${(location * 100).toFixed(2)}%`)
    .join(', ');
  const at = `${(halo.cx * 100).toFixed(2)}% ${(halo.cy * 100).toFixed(2)}%`;
  return {
    backgroundImage: `radial-gradient(50% 50% at ${at}, ${stops})`,
    backgroundSize: `${halo.width} ${halo.height}`,
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  } as const;
}

/**
 * The pool that fades the field out around a block of running text.
 *
 * The HERO'S mechanism, reused: an ellipse of ground with a long smoothstep
 * feather — a cloud, not a shape. A rounded rectangle with a blurred edge was
 * tried here and looked exactly like what it was: a pane of glass laid over the
 * artwork. The ellipse doesn't, because nothing about it lines up with the
 * block's own edges.
 *
 * The ELEMENT carries the size (`TEXT_POOL_INSET`) and the gradient fills it —
 * a background is clipped to its element, so a gradient sized past its own box
 * gets its feather cut off, which is a hard rectangle. That shipped once.
 *
 * Geometry: the element is the block doubled plus a floor, so the inscribed
 * ellipse's semi-axes exceed the block's own half-diagonal reach — every corner
 * lands under √0.5 ≈ 0.707, inside `opaqueTo`, on solid ground. The feather
 * then runs the remaining ~28% of the semi-axis: ~170px around a heading, more
 * around a list. `textPoolCornerDistance` is the number; the plate test walks
 * it across real block shapes.
 */
export const TEXT_POOL: TextHalo = {
  cx: 0.5,
  cy: 0.5,
  width: '100%',
  height: '100%',
  opaqueTo: 0.72,
};

/** Half of what the pool adds per side: applied as insets on an `absolute`
 *  element it yields a box of `200% + 180px` by `200% + 140px` around the
 *  block. The px term is a floor so a single line still gets a real feather. */
export const TEXT_POOL_INSET = {
  x: 'calc(-50% - 90px)',
  y: 'calc(-50% - 70px)',
} as const;

/** Where a block's corner falls on its pool, as normalised elliptical distance.
 *  `boxMultiple` is 2 for TEXT_POOL; pads are the px floor. */
export function textPoolCornerDistance(boxMultiple: number, padX = 0, padY = 0, w = 1, h = 1): number {
  return haloDistance(w / 2, h / 2, (boxMultiple * w + padX) / 2, (boxMultiple * h + padY) / 2);
}
